import {getFirstSelectedEmailMarketingCampaign, getSelectedItemIdAtIndex, INPUT_SELECTED_ITEM_ID_INDEX, ITEM_TYPES} from '@Libraries/email-marketing-campaigns-library';
import {MY_STUFF_HASHES} from '@Libraries/my-stuff-library';
import {PMW_COLORS_100} from '@Utils/color.util';
import type {EmailMarketingCampaign, EmailMarketingCampaignPerformanceState, Metrics, PerformanceInputs, SingleCampaignMetrics, UserMailingList} from './email-marketing-campaign-performance.types';
import {COMPARISON_VIEW_SCREEN_WIDTH_BREAKPOINT_PX} from './email-marketing-campaign-performance.types';

export const PERFORMANCE_ERROR_CODES = {
  PREMIUM_UNDERLEVELED: 'premium-underleveled',
} as const;

export const METRIC_TYPES = {
  DELIVERY: 'delivery',
  CLICK: 'click',
  OPEN: 'open',
} as const;

export function getMetricDetailsForType(metrics: SingleCampaignMetrics, metricType: string): Metrics {
  switch (metricType) {
    case METRIC_TYPES.DELIVERY:
      return metrics.delivery;
    case METRIC_TYPES.OPEN:
      return metrics.open;
    case METRIC_TYPES.CLICK:
      return metrics.click;
    default:
      throw new Error(`Unknown metric type: ${metricType}`);
  }
}

export const INPUT_HASH_INDEX = {
  ITEM_TYPE: 2,
  FIRST_SELECTED_ITEM_ID: 3,
  SECOND_SELECTED_ITEM_ID: 4,
} as const;

export const DEFAULT_CAMPAIGN_METRIC_GRAPH_COLORS = {
  Primary: PMW_COLORS_100.PRIMARY,
  Secondary: PMW_COLORS_100.SECONDARY,
  Tertiary: PMW_COLORS_100.SUCCESS,
} as const;

function narrowSelectedItemId(rawSelectedItemId: unknown): string | null {
  return typeof rawSelectedItemId === 'string' ? rawSelectedItemId : null;
}

export function getFirstSelectedItemId(selectedItemIds: string[]): string | null {
  return narrowSelectedItemId(getSelectedItemIdAtIndex(selectedItemIds, INPUT_SELECTED_ITEM_ID_INDEX.FIRST_ID));
}

export function getSecondSelectedItemId(selectedItemIds: string[]): string | null {
  return narrowSelectedItemId(getSelectedItemIdAtIndex(selectedItemIds, INPUT_SELECTED_ITEM_ID_INDEX.SECOND_ID));
}

function getEmailMarketingCampaignsPerformanceStateFromReduxStore(): EmailMarketingCampaignPerformanceState {
  return window.PMW.redux.store.getState().emailMarketingCampaignsPerformance;
}

export function getEmailMarketingCampaignSelectedItemTypeFromStore(): string {
  return getEmailMarketingCampaignsPerformanceStateFromReduxStore().itemType;
}

export function getFirstSelectedMailingList(): UserMailingList | null {
  const {selectedItemIds, mailingLists} = getEmailMarketingCampaignsPerformanceStateFromReduxStore();
  const firstSelectedItemId = getFirstSelectedItemId(selectedItemIds);

  if (firstSelectedItemId === null) {
    return null;
  }

  return mailingLists[firstSelectedItemId] ?? null;
}

function isEmailMarketingCampaign(item: unknown): item is EmailMarketingCampaign {
  return item !== null && typeof item === 'object' && 'publishOn' in item;
}

export function getFirstSelectedItem(): EmailMarketingCampaign | UserMailingList | null {
  if (getEmailMarketingCampaignSelectedItemTypeFromStore() === ITEM_TYPES.CAMPAIGNS) {
    const firstSelectedCampaign: unknown = getFirstSelectedEmailMarketingCampaign();
    return isEmailMarketingCampaign(firstSelectedCampaign) ? firstSelectedCampaign : null;
  }

  return getFirstSelectedMailingList();
}

export function updateWindowLocationHash(itemType: string, selectedItemIds: string[], shouldReplaceHistoryEntry = false): void {
  let computedHash = `${MY_STUFF_HASHES.emailCampaignPerformance}/${itemType}`;

  if (selectedItemIds.length > 0) {
    computedHash += `/${selectedItemIds[INPUT_SELECTED_ITEM_ID_INDEX.FIRST_ID]}`;
  }
  if (selectedItemIds.length > 1) {
    computedHash += `/${selectedItemIds[INPUT_SELECTED_ITEM_ID_INDEX.SECOND_ID]}`;
  }

  if (shouldReplaceHistoryEntry) {
    window.history.replaceState(window.history.state, '', computedHash);
    return;
  }

  window.location.hash = computedHash;
}

const isMobileScreenWidth = (): boolean => {
  return window.innerWidth <= COMPARISON_VIEW_SCREEN_WIDTH_BREAKPOINT_PX;
};

export function getPerformanceInputsFromURL(): PerformanceInputs {
  const segments = window.location.hash.split('/');
  const itemType = segments[INPUT_HASH_INDEX.ITEM_TYPE] ?? ITEM_TYPES.CAMPAIGNS;

  const requestedItemIds = segments.slice(INPUT_HASH_INDEX.FIRST_SELECTED_ITEM_ID, INPUT_HASH_INDEX.SECOND_SELECTED_ITEM_ID + 1);
  const uniqueItemIds = [...new Set(requestedItemIds)];

  if (isMobileScreenWidth()) {
    return {itemType, selectedItemIds: uniqueItemIds.slice(0, 1)};
  }

  return {itemType, selectedItemIds: uniqueItemIds};
}
